import { useQueryClient } from "@tanstack/react-query";
import i18next, { t } from "i18next";
import { isEqual } from "lodash";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import arrowIcon from "../../../assets/images/chevron-left.svg";
import { GeneralButton } from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import { useGetData, usePostData } from "../../../hooks";
import { IStepFields } from "../../../interface/MedicalRecord";
import { resetFileCount } from "../../../redux/reducers/investigationFilesSlice";
import { clearFollowUp, setFollowUp } from "../../../redux/reducers/medicalRecordSlice";
import { RootState } from "../../../redux/store";
import { useToast } from "../../../Services/ToastContext";
import { getGridColumnClass } from "../../../utils/fileUtils";
import { FieldRenderer } from "../component/FieldRenderer/FieldRenderer";
import PatientInfo from "../component/PatientInfo/PatientInfo";
import MedicalRecordHeader from "../MedicalRecordHeader/MedicalRecordHeader";
import { cleanEmptyStrings, cleanEmptyValues, stringifyValues } from "../utils";
import MedicalRecordInfo from "./MedicalRecordInfo";
import MultiStepOfVisit from "./MultiStepOfVisit";

const NewFollowUp: React.FC = () => {
  const { PatientId, recordId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [current, setCurrent] = useState(0);
  const [currentSubStep, setCurrentSubStep] = useState(0);
  const getPatientRecord = useGetData<any>(
    EndPoints.GetMedicalRecord,
    {},
    recordId,
  );

  const { data: visitStepsData } = useGetData<any>(
    EndPoints.GetVisitTemplate,
    {},
    recordId,
  );

  const { data } = getPatientRecord;

  const lastStepIndex = (visitStepsData?.Data?.Steps?.length ?? 1) - 1;
  const isFirstStep = current === 0;

  const currentStepData = visitStepsData?.Data?.Steps?.[current ?? 0];
  const currentSubStepData = currentStepData?.InverseParentSteps?.[currentSubStep];


  const subCount = currentStepData?.InverseParentSteps?.length ?? 0;

  const isLastStep =
    current === lastStepIndex &&
    (subCount === 0 || currentSubStep === subCount - 1);

  const goNext = () => {
    const subCount = currentStepData?.InverseParentSteps?.length ?? 0;

    if (subCount > 0 && currentSubStep < subCount - 1) {
      setCurrentSubStep((prev) => prev + 1);
    } else {
      setCurrent((prev: number) => {

        return prev + 1;
      });
      setCurrentSubStep(0);
    }


  };
  const goBack = () => {
    const subCount = currentStepData?.InverseParentSteps?.length ?? 0;

    if (subCount > 0 && currentSubStep > 0) {
      setCurrentSubStep((prev) => prev - 1);
    } else if (current > 0) {
      setCurrent((prev) => prev - 1);

      const prevStepSubCount =
        visitStepsData?.Data?.Steps?.[current - 1]?.InverseParentSteps?.length ??
        0;



      setCurrentSubStep(
        prevStepSubCount > 0 ? prevStepSubCount - 1 : 0,
      );
    }

  };

  const methods = useForm<any>({
    mode: "onChange",
    defaultValues: {
      Attributes: {},
    },
  });

  const { handleSubmit, control } = methods;
  const queryClient = useQueryClient();

  const { mutate, isPending } = usePostData(EndPoints.CreateVisit);

  const onSubmit = (values: any) => {



    const cleanedValues = cleanEmptyStrings(values);



    const { Attributes, ...rest } = cleanedValues;



    const cleanedAttributes = Object.fromEntries(
      Object.entries({ ...Attributes }).filter(
        ([key, value]) => !key.includes("-option") &&
          value !== null &&

          value !== undefined &&
          !(typeof value === "string" && value.trim() === "")
      )
    );

    values.PatientId = Number(PatientId);

    const bodyData = {
      PatientId: PatientId,
      MedicalRecordId: recordId,
      ...cleanEmptyStrings(cleanEmptyValues(rest)),
      Attributes: stringifyValues(cleanedAttributes),
    };

    mutate(
      bodyData,
      {
        onSuccess: () => {
          dispatch(setFollowUp(null));
          setCurrent(0)
          methods.reset()
          // dispatch(clearFollowUp());
          // dispatch(resetFileCount());
          queryClient.invalidateQueries({
            queryKey: [EndPoints.GetMedicalRecord, recordId],
            exact: false,
          });
          showToast(t("success"), "success");
          navigate(`/patients_list/${PatientId}/medical-records`);
        },
        onError: (error) => {
          showToast(error.message || t("error"), "error");
        },
      },
    );
  };



  //set state on form change
  const values = useWatch({ control });
  const prev = useRef(values);

  const followUp: any = useSelector(
    (state: RootState) => state.medicalRecord.followUp,
  );

  // useEffect(() => {
  //   methods.reset(followUp);
  // }, [current,currentSubStep]);



  return (
    <div className="box text-primary-textFont mx-auto flex h-[100dvh] flex-col gap-6 !pt-8">
      <MedicalRecordHeader
        onBack={() => {
          methods.reset()
          dispatch(clearFollowUp());
        }}
        backRoute={
          PatientId ? `/patients_list/${PatientId}/medical-records` : undefined
        }
        selectedTemplateName={data?.Data?.MedicalHistoryType?.Name}
        title={t("add_follow_up_to_medical_report")}
      />
      <div className="flex min-h-0 flex-1 flex-col">
        <PatientInfo />
        <div className="flex min-h-0 flex-1 gap-10">
          <div className="flex w-80 flex-col">
            <MultiStepOfVisit
              steps={visitStepsData?.Data?.Steps}
              currentStep={current}
              setCurrentStep={setCurrent}
              currentSubStep={currentSubStep}
              setCurrentSubStep={setCurrentSubStep}
            />
            <MedicalRecordInfo />
          </div>
          <div className="mx-auto flex min-h-0 w-fit flex-1 flex-col gap-6">
            <h1 className="font-bold">{t("new_visit")}</h1>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:w-[96%]">
              <div className="min-h-0 w-full flex-1 flex-col gap-4 overflow-y-auto">
                <FormProvider {...methods}>
                  <form className="mt-2 grid grid-cols-12 gap-4">
                    {currentStepData?.StepFields?.map(
                      (field: IStepFields, i: number) => {
                        return (
                          <div
                            key={field.Id || field.FieldId || i}
                            className={`h-fit ${getGridColumnClass(field?.ColumnSize || 0)}`}
                          >
                            <FieldRenderer field={field} />
                          </div>
                        );
                      },
                    )}

                    {currentSubStepData?.StepFields?.length > 0 && currentSubStepData?.StepFields?.map(
                      (field: IStepFields, i: number) => {
                        return (
                          <div
                            key={field.Id || field.FieldId || i}
                            className={`h-fit ${getGridColumnClass(field?.ColumnSize || 0)}`}
                          >
                            <FieldRenderer field={field} />
                          </div>
                        );
                      },
                    )}
                  </form>
                </FormProvider>
              </div>

              <div className="sticky bottom-0 z-10 flex flex-col-reverse gap-2 px-2 py-4 backdrop-blur sm:flex-row sm:items-center sm:justify-end">
                {!isFirstStep && (
                  <GeneralButton
                    onClick={goBack}
                    disabled={isPending}
                    className="w-full flex-row-reverse border border-primary !pe-6 sm:w-fit"
                    title={
                      <div className="flex items-center justify-start gap-2">
                        <img
                          className={
                            i18next?.language === "ar" ? "" : "rotate-180"
                          }
                          alt="back"
                          src={arrowIcon}
                        />
                        <p>{t("back")}</p>
                      </div>
                    }
                    color="lite"
                  />
                )}

                <GeneralButton
                  onClick={() => {
                    dispatch(resetFileCount());
                    methods.reset()
                    dispatch(clearFollowUp());
                    navigate(`/patients_list/${PatientId}/medical-records`)
                  }}
                  disabled={isPending}
                  color="lite"
                  title={t("cancel")}
                  className="!text-primary-text w-full !bg-white sm:ms-auto sm:w-fit"
                />

                {!isLastStep ? (
                  <GeneralButton
                    onClick={goNext}
                    disabled={isPending}
                    className="w-full border border-primary !px-4 sm:w-fit"
                    title={
                      <div className="flex items-center justify-center gap-2">
                        <p>{t("next")}</p>
                        <img
                          className={`${i18next?.language !== "ar" ? "" : "rotate-180"
                            } brightness-0 invert`}
                          alt="next"
                          src={arrowIcon}
                        />
                      </div>
                    }
                  />
                ) : (
                  <GeneralButton
                    onClick={handleSubmit(onSubmit)}
                    disabled={isPending}
                    isLoading={isPending}
                    className="w-full !px-6 sm:w-fit"
                    title={isPending ? t("loading") : t("submit")}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewFollowUp;
